<script lang="ts" setup>
/* eslint-disable no-console */
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { brand, brandConfig, features } from '@/config/brands'
import { pushAxonEvent } from '@/utils/axonTrack'

const ADSENSE_CLIENT_ID = brandConfig.texts?.adsenseClientId || ''
const ADSENSE_SLOT_ID = brandConfig.texts?.adsenseSlotId || ''
const AD_HOST_KEYWORD = brandConfig.texts?.adSenseHostKeyword || ''
const ADSTERRA_KEY = brandConfig.texts?.adsterraKey || ''
const VIGNETTE_ZONE = brandConfig.texts?.vignetteZone || ''
const HILLTOPADS_SRC = brandConfig.texts?.hilltopadsSrc || ''
const tag = `[Ad-${brand}]`

let warnedHost = false

const enableAdHost = computed(() => {
  if (!AD_HOST_KEYWORD)
    return true
  const keywords = AD_HOST_KEYWORD.split(',').map(k => k.trim())
  return keywords.some(k => location.hostname.includes(k))
})

const isReady = computed(() => {
  if (!features?.enableAdSense) {
    if (!warnedHost) {
      warnedHost = true
      console.warn(tag, '广告功能未开启')
    }
    return false
  }
  if (!enableAdHost.value) {
    if (!warnedHost) {
      warnedHost = true
      console.warn(tag, `域名不包含 ${AD_HOST_KEYWORD}`)
    }
    return false
  }
  return true
})

type AdSource = 'adsense' | 'adsterra' | 'vignette' | 'hilltopads'

function pickRandomSource(): AdSource | null {
  const sources: AdSource[] = []
  if (ADSENSE_CLIENT_ID && ADSENSE_SLOT_ID)
    sources.push('adsense')
  if (ADSTERRA_KEY)
    sources.push('adsterra')
  if (VIGNETTE_ZONE)
    sources.push('vignette')
  if (HILLTOPADS_SRC)
    sources.push('hilltopads')
  if (sources.length === 0)
    return null
  return sources[Math.floor(Math.random() * sources.length)]
}

function pickFallbackSource(): AdSource | null {
  const sources: AdSource[] = []
  if (ADSTERRA_KEY)
    sources.push('adsterra')
  if (VIGNETTE_ZONE)
    sources.push('vignette')
  if (sources.length === 0)
    return null
  return sources[Math.floor(Math.random() * sources.length)]
}

const source = ref<AdSource | null>(null)
const currentAd = ref<AdSource | null>(null)

const adRef = ref<HTMLElement>()

let statusObserver: MutationObserver | null = null
let statusTimer: ReturnType<typeof setTimeout> | null = null

function cleanup() {
  statusObserver?.disconnect()
  statusObserver = null
  if (statusTimer) {
    clearTimeout(statusTimer)
    statusTimer = null
  }
}

function reportVisible() {
  pushAxonEvent('purchase', {
    currency: 'USD',
    value: 0.001,
    transaction_id: `ad_${ADSENSE_SLOT_ID}_${Date.now()}`,
    items: [{
      item_id: `ad_slot_${ADSENSE_SLOT_ID}`,
      item_name: `AdSense_Slot_${ADSENSE_SLOT_ID}`,
      price: 0,
      quantity: 1,
    }],
  }, { is_taboola_ad_purchase: true })
}

function observeVisibility(el: HTMLElement) {
  if (el.getBoundingClientRect().top < window.innerHeight) {
    reportVisible()
    return
  }
  const visObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting)
        continue
      reportVisible()
      visObserver.disconnect()
    }
  }, { threshold: 0.5 })
  visObserver.observe(el)
}

function loadAdsense(el: HTMLElement) {
  const ins = el.querySelector('ins.adsbygoogle') as HTMLElement | null
  if (!ins)
    return

  try {
    ;(window as any).adsbygoogle = (window as any).adsbygoogle || []
    ;(window as any).adsbygoogle.push({})
    console.log(tag, 'AdSense push 已执行', `slot=${ADSENSE_SLOT_ID}`)
  }
  catch (err) {
    console.error(tag, 'AdSense push 失败', err, `slot=${ADSENSE_SLOT_ID}`)
    switchToFallback()
    return
  }

  statusObserver = new MutationObserver(() => {
    const status = ins.getAttribute('data-ad-status')
    if (status === 'filled') {
      console.log(tag, 'AdSense 填充成功', `slot=${ADSENSE_SLOT_ID}`)
      cleanup()
      observeVisibility(el)
    }
    else if (status === 'unfilled' || status === 'unfill-optimized') {
      console.warn(tag, `AdSense ${status}，切换备选`, `slot=${ADSENSE_SLOT_ID}`)
      statusObserver?.disconnect()
      switchToFallback()
    }
  })
  statusObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })

  statusTimer = setTimeout(() => {
    if (ins.getAttribute('data-ad-status') !== 'filled') {
      console.warn(tag, 'AdSense 超时 (5s) 未填充，切换备选', `slot=${ADSENSE_SLOT_ID}`)
      statusObserver?.disconnect()
      switchToFallback()
    }
  }, 5000)
}

function loadAdsterra(el: HTMLElement) {
  ;(window as any).atOptions = {
    key: ADSTERRA_KEY,
    format: 'iframe',
    height: 250,
    width: 300,
    params: {},
  }
  const script = document.createElement('script')
  script.src = `https://www.highperformanceformat.com/${ADSTERRA_KEY}/invoke.js`
  script.async = true
  script.onload = () => console.log(tag, 'Adsterra 脚本已加载')
  script.onerror = () => console.warn(tag, 'Adsterra 脚本加载失败')
  el.appendChild(script)
}

function loadVignette(el: HTMLElement) {
  const sandbox = el.querySelector('.novel-inline-ad-sandbox') as HTMLElement | null
  if (!sandbox)
    return

  const iframe = document.createElement('iframe')
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin')
  iframe.setAttribute('scrolling', 'no')
  iframe.style.cssText = 'width:300px;height:250px;border:none;overflow:hidden;display:block;margin:0 auto;background:transparent;'
  iframe.srcdoc = `<!DOCTYPE html><html><head><style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:transparent;overflow:hidden;width:300px;height:250px}
</style></head><body>
<script data-zone="${VIGNETTE_ZONE}" src="https://n6wxm.com/vignette.min.js"><\/script>
<script src="https://quge5.com/88/tag.min.js" data-zone="245686" async data-cfasync="false"><\/script>
</body></html>`
  sandbox.appendChild(iframe)
  console.log(tag, 'Vignette iframe 已注入', `zone=${VIGNETTE_ZONE}`)
}

function loadHilltopads(el: HTMLElement) {
  const sandbox = el.querySelector('.novel-inline-ad-sandbox') as HTMLElement | null
  if (!sandbox)
    return

  const lockCss = `
* { margin:0; padding:0; box-sizing:border-box; }
body { background:transparent; display:flex; justify-content:center; align-items:center; min-height:250px; }
.novel-inline-ad-sandbox { width:300px; height:250px; overflow:hidden; position:relative; margin:0 auto; }
.novel-inline-ad-sandbox div, .novel-inline-ad-sandbox iframe, .novel-inline-ad-sandbox a, .novel-inline-ad-sandbox img { position:relative !important; top:0 !important; left:0 !important; width:100% !important; height:100% !important; transform:none !important; }
`
  const scriptCode = `
(function(nfpv){
  var d = document,
      s = d.createElement('script'),
      l = d.scripts[d.scripts.length - 1];
  s.settings = nfpv || {};
  s.src = "${HILLTOPADS_SRC}";
  s.async = true;
  s.referrerPolicy = "no-referrer-when-downgrade";
  l.parentNode.insertBefore(s, l);
})({})
`

  const iframe = document.createElement('iframe')
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin')
  iframe.setAttribute('scrolling', 'no')
  iframe.style.cssText = 'width:300px;height:250px;border:none;overflow:hidden;display:block;margin:0 auto;'
  iframe.srcdoc = `<style>${lockCss}</style><div class="novel-inline-ad-sandbox"><script>${scriptCode}<\/script></div>`
  sandbox.appendChild(iframe)
  console.log(tag, 'HilltopAds iframe 已注入')
}

function switchToFallback() {
  cleanup()
  const fallback = pickFallbackSource()
  if (!fallback) {
    console.warn(tag, '无可用备选广告')
    return
  }
  currentAd.value = fallback
  nextTick(() => {
    const el = adRef.value
    if (!el)
      return
    if (fallback === 'adsterra')
      loadAdsterra(el)
    else if (fallback === 'vignette')
      loadVignette(el)
  })
}

onMounted(() => {
  if (!isReady.value)
    return

  source.value = pickRandomSource()
  if (!source.value) {
    console.warn(tag, '所有广告源配置缺失')
    return
  }
  currentAd.value = source.value

  nextTick(() => {
    const el = adRef.value
    if (!el)
      return

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting)
          continue
        observer.disconnect()
        console.log(tag, `本次广告: ${source.value}`, `slot=${ADSENSE_SLOT_ID}`)

        if (source.value === 'adsense')
          loadAdsense(el)
        else if (source.value === 'adsterra')
          loadAdsterra(el)
        else if (source.value === 'vignette')
          loadVignette(el)
        else if (source.value === 'hilltopads')
          loadHilltopads(el)
      }
    }, { rootMargin: '0px' })
    observer.observe(el)
  })
})

onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div v-if="currentAd === 'adsense'" ref="adRef" class="chapter-ad-wrapper">
    <ins
      class="adsbygoogle"
      style="display:block; width:100%"
      data-ad-layout="in-article"
      data-ad-format="fluid"
      :data-ad-client="ADSENSE_CLIENT_ID"
      :data-ad-slot="ADSENSE_SLOT_ID"
    />
  </div>
  <div v-else-if="currentAd === 'vignette' || currentAd === 'hilltopads'" ref="adRef" class="chapter-ad-wrapper">
    <div class="novel-inline-ad-sandbox" />
  </div>
  <div v-else-if="currentAd" ref="adRef" class="chapter-ad-wrapper" />
</template>

<style scoped>
.chapter-ad-wrapper {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  width: 100%;
}

.novel-inline-ad-sandbox {
  width: 300px;
  height: 250px;
  overflow: hidden;
  margin: 0 auto;
}
</style>
